import { useState, useRef, useCallback } from "react";
import { Camera, Upload, X, RotateCcw, Check } from "lucide-react";
import { hasNativeCamera, takeNativePhoto, pickNativeGallery } from "@/lib/native-camera";

type Props = {
  onCapture: (file: File) => void;
  label?: string;
  className?: string;
};

export default function PhotoCapture({ onCapture, label = "Take Photo", className = "" }: Props) {
  const [mode, setMode] = useState<"idle" | "camera" | "preview">("idle");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = useCallback(async () => {
    // Native Capacitor: use native camera plugin
    if (hasNativeCamera()) {
      try {
        const file = await takeNativePhoto();
        setCapturedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setMode("preview");
      } catch (err) {
        console.error("Native camera error:", err);
      }
      return;
    }

    // Mobile web: capture="environment" opens native camera app
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && cameraInputRef.current) {
      cameraInputRef.current.click();
      return;
    }
    // Desktop: use in-app camera
    navigator.mediaDevices
      .getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1600 }, height: { ideal: 1200 } },
      })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
        setMode("camera");
      })
      .catch((err) => {
        console.error("Camera access denied:", err);
        galleryInputRef.current?.click();
      });
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    if (!canvas.width || !canvas.height) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);
    stopCamera();

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
        setCapturedFile(file);
        setPreviewUrl(URL.createObjectURL(blob));
        setMode("preview");
      },
      "image/jpeg",
      0.85
    );
  }, [stopCamera]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCapturedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setMode("preview");
  }, []);

  const retake = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setCapturedFile(null);
    setMode("idle");
  }, [previewUrl]);

  const confirm = useCallback(() => {
    if (capturedFile) {
      onCapture(capturedFile);
    }
  }, [capturedFile, onCapture]);

  const cancel = useCallback(() => {
    stopCamera();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setCapturedFile(null);
    setMode("idle");
  }, [stopCamera, previewUrl]);

  // Idle state - show capture buttons
  if (mode === "idle") {
    return (
      <div className={`space-y-3 ${className}`}>
        {label && <label className="block text-sm font-medium text-foreground">{label}</label>}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={startCamera}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-4 bg-forest-600 hover:bg-forest-700 text-white rounded-xl font-medium transition-colors text-sm"
          >
            <Camera size={20} />
            Camera
          </button>
          <button
            type="button"
            onClick={async () => {
              if (hasNativeCamera()) {
                try {
                  const file = await pickNativeGallery();
                  setCapturedFile(file);
                  setPreviewUrl(URL.createObjectURL(file));
                  setMode("preview");
                } catch (err) {
                  console.error("Native gallery error:", err);
                }
              } else {
                galleryInputRef.current?.click();
              }
            }}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-4 bg-secondary hover:bg-secondary/80 text-foreground rounded-xl font-medium transition-colors text-sm border border-border"
          >
            <Upload size={20} />
            Gallery
          </button>
        </div>
        {/* Gallery: no capture → opens photo library / gallery on mobile */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        {/* Camera on mobile: capture="environment" → opens native camera */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  // Camera active
  if (mode === "camera") {
    return (
      <div className={`relative ${className}`}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full rounded-xl bg-black aspect-[4/3] object-cover"
        />
        <canvas ref={canvasRef} className="hidden" />

        <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={cancel}
            className="w-12 h-12 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
          >
            <X size={20} />
          </button>
          <button
            type="button"
            onClick={capturePhoto}
            className="w-16 h-16 bg-white rounded-full flex items-center justify-center border-4 border-forest-600 active:scale-95 transition-transform"
          >
            <div className="w-12 h-12 bg-forest-600 rounded-full" />
          </button>
          <div className="w-12" /> {/* spacer */}
        </div>
      </div>
    );
  }

  // Preview state
  return (
    <div className={`relative ${className}`}>
      {previewUrl && (
        <img
          src={previewUrl}
          alt="Captured photo"
          className="w-full rounded-xl aspect-[4/3] object-cover"
        />
      )}

      <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={retake}
          className="flex items-center gap-2 px-4 py-3 bg-black/50 backdrop-blur-sm rounded-xl text-white font-medium text-sm"
        >
          <RotateCcw size={16} />
          Retake
        </button>
        <button
          type="button"
          onClick={confirm}
          className="flex items-center gap-2 px-6 py-3 bg-forest-600 rounded-xl text-white font-medium text-sm"
        >
          <Check size={16} />
          Use Photo
        </button>
      </div>
    </div>
  );
}
